import {
  useEffect,
  useState,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from "react";
import { Link } from "react-router";
import { useScopeParams } from "@/hooks/useScopeParams";
import { loadMarkdown, getManifest } from "@/utils/markdownLoader";
import {
  type HubCardData,
  type HubSectionData,
  type LatestRelease,
  type AccentIdx,
  ACCENT_GRADIENTS,
  HUB_SECTIONS,
} from "./WikiHubData";
import {
  manifestDateValue,
  formatManifestDate,
} from "./release-notes/releaseNoteMarkdown";
import { scrollToWikiTop } from "./utils/scrollToWikiTop";

// ── Icon media ────────────────────────────────────────────────────────────────

function IconMedia({
  Icon,
  accent = 0,
}: {
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  accent?: AccentIdx;
}) {
  return (
    <div
      className="flex h-full w-full items-center justify-center"
      style={{ background: ACCENT_GRADIENTS[accent] }}
    >
      <Icon className="h-[52px] w-[52px] opacity-55" />
    </div>
  );
}

// ── Badge overlays ────────────────────────────────────────────────────────────

function PlayIcon() {
  return (
    <svg viewBox="0 0 12 12" className="h-[10px] w-[10px] fill-current">
      <path d="M3 2 L10 6 L3 10 Z" />
    </svg>
  );
}

function TypeBadge({ type }: { type: HubCardData["type"] }) {
  const isVideo = type === "video";
  const label = isVideo
    ? "Video"
    : type === "walkthrough"
      ? "Walkthrough"
      : "Article";
  return (
    <div
      className={`absolute left-3 top-3 flex items-center gap-1 rounded px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide backdrop-blur-sm ${
        isVideo
          ? "bg-[rgba(23,49,58,0.88)] text-white"
          : "bg-[rgba(255,255,255,0.95)] text-[#4b6570]"
      }`}
    >
      {isVideo && <PlayIcon />}
      {label}
    </div>
  );
}

function NewBadge() {
  return (
    <div className="absolute right-3 top-3 rounded-full bg-[#00a3b4] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
      NEW
    </div>
  );
}

function VideoDurationBadge({ duration }: { duration: string }) {
  return (
    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-sm">
      <PlayIcon />
      {duration}
    </div>
  );
}

function formatVideoDuration(seconds: number) {
  const mins = Math.floor(Math.max(0, Math.round(seconds)) / 60);
  const secs = Math.max(0, Math.round(seconds)) % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

// ── Card ─────────────────────────────────────────────────────────────────────

function HubCard({
  card,
  buildUrl,
}: {
  card: HubCardData;
  buildUrl: (path?: string) => string;
}) {
  const [videoDuration, setVideoDuration] = useState<string | null>(
    card.duration ?? null,
  );

  let media: ReactNode;
  if (card.videoSrc) {
    media = (
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        crossOrigin="anonymous"
        onLoadedMetadata={(e) => {
          if (!card.duration)
            setVideoDuration(formatVideoDuration(e.currentTarget.duration));
        }}
        className="h-full w-full object-cover"
      >
        <source src={card.videoSrc} type="video/mp4" />
      </video>
    );
  } else if (card.imagery === "image" && card.imageSrc) {
    media = (
      <img
        src={card.imageSrc}
        alt={card.title}
        crossOrigin="anonymous"
        className="h-full w-full object-cover"
      />
    );
  } else if (card.imagery === "icon" && card.icon) {
    media = <IconMedia Icon={card.icon} accent={card.accent ?? 0} />;
  } else {
    media = (
      <div
        className="h-full w-full"
        style={{ background: ACCENT_GRADIENTS[card.accent ?? 0] }}
      />
    );
  }

  const metaLine =
    card.type === "video" ? (
      videoDuration ? (
        <span>{videoDuration} watch</span>
      ) : (
        <span>Video</span>
      )
    ) : (
      <>
        {card.readTime && <span>{card.readTime} read</span>}
        <span className="inline-block h-1 w-1 rounded-full bg-current opacity-40" />
        <span>
          {card.type === "walkthrough" ? "Step-by-step" : "Reference"}
        </span>
      </>
    );

  const inner = (
    <>
      <div className="relative aspect-[16/10] shrink-0 overflow-hidden">
        {media}
        <TypeBadge type={card.type} />
        {card.isNew && <NewBadge />}
        {card.type === "video" && videoDuration && (
          <VideoDurationBadge duration={videoDuration} />
        )}
      </div>
      <div className="flex flex-1 flex-col px-[18px] pb-5 pt-4">
        <h3 className="m-0 text-[17px] font-semibold leading-snug tracking-tight text-[#17313a]">
          {card.title}
        </h3>
        <p className="mt-2 flex-1 text-[13.5px] leading-[1.55] text-[#6b8390]">
          {card.description}
        </p>
        <div className="mt-3.5 flex items-center gap-2 text-[12px] font-medium text-[#8fa8b2]">
          {metaLine}
        </div>
      </div>
    </>
  );

  return card.path ? (
    <Link to={buildUrl(`wiki/guides/${card.path}`)} className="wiki-hub-card">
      {inner}
    </Link>
  ) : (
    <div className="wiki-hub-card wiki-hub-card--preview">{inner}</div>
  );
}

// ── Section ───────────────────────────────────────────────────────────────────

function HubSection({
  section,
  buildUrl,
}: {
  section: HubSectionData;
  buildUrl: (path?: string) => string;
}) {
  return (
    <section className="mt-6 border-t border-[#e7eaec] pt-12">
      <div className="grid gap-10 grid-cols-[220px_1fr]">
        <div className="sticky top-[72px] self-start">
          <div className="rounded-r-xl border-l-[3px] border-l-[#0c98d0] bg-[rgba(12,152,208,0.04)] py-5 pl-4 pr-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#0c98d0]">
              {section.eyebrow}
            </p>
            <h2 className="mt-2 text-[22px] font-semibold leading-[1.15] tracking-tight text-[#17313a]">
              {section.title}
            </h2>
            <p className="mt-3 text-[12px] text-[#8fa8b2]">
              <strong className="font-semibold text-[#17313a]">
                {section.cards.length}
              </strong>{" "}
              {section.cards.length === 1 ? "guide" : "guides"}
            </p>
          </div>
        </div>

        <div className="grid gap-[18px] grid-cols-[repeat(auto-fill,minmax(260px,1fr))]">
          {section.cards.map((card) => (
            <HubCard key={card.id} card={card} buildUrl={buildUrl} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Hero ─────────────────────────────────────────────────────────────────────

function EditorialHero({
  buildUrl,
  latestRelease,
}: {
  buildUrl: (path?: string) => string;
  latestRelease: LatestRelease | null;
}) {
  const recentCards = HUB_SECTIONS.flatMap((s) =>
    s.cards.map((c) => ({ ...c, sectionEyebrow: s.eyebrow })),
  ).filter((c) => c.isNew && c.path);

  return (
    <div className="grid items-start gap-10 pb-12 pt-14 grid-cols-[1fr_1.2fr]">
      {/* Left: editorial copy */}
      <div className="max-w-[540px]">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0c98d0]">
          Vivo Wiki
        </p>
        <h1 className="mt-3.5 text-[48px] font-medium leading-[1.05] tracking-[-0.03em] text-[#17313a]">
          Everything you need to run a smarter trial.
        </h1>
        <p className="mt-4 max-w-[480px] text-[17px] leading-relaxed text-[#6b8390]">
          Guides, walkthroughs and short videos for every screen — from your
          first login to building dashboards across a multi-site trial.
        </p>
      </div>

      {/* Right: What's New panel */}
      <div className="overflow-hidden rounded-[14px] border border-[#e7eaec] bg-white">
        <div className="border-b border-[#e7eaec] px-5 py-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0c98d0]">
            What&apos;s new
          </p>
        </div>

        {latestRelease && (
          <Link
            to={buildUrl("wiki/release-notes/all-release-notes")}
            className="group block border-b border-[#d4eaf5] bg-[#f0f9fd] px-5 py-4 transition-colors duration-100 hover:bg-[#e8f5fb]"
          >
            <div className="mb-1.5 flex items-baseline justify-between gap-3">
              <div className="flex items-baseline gap-2">
                <span className="text-[15px] font-semibold text-[#17313a] transition-colors duration-100 group-hover:text-[#0c98d0]">
                  Vivo v{latestRelease.version}
                </span>
                <span className="text-[12px] text-[#8fa8b2]">
                  {latestRelease.date}
                </span>
              </div>
              <span className="shrink-0 text-[11px] text-[#8fa8b2] transition-colors duration-100 group-hover:text-[#0c98d0]">
                All releases →
              </span>
            </div>
            <p className="text-[13px] leading-[1.55] text-[#455b66]">
              {latestRelease.description}
            </p>
          </Link>
        )}

        <div className="flex flex-col divide-y divide-[#f0f2f4]">
          {recentCards.map((card) => {
            const meta = card.type === "video" ? card.duration : card.readTime;
            const typeLabel =
              card.type === "video"
                ? "Video"
                : card.type === "walkthrough"
                  ? "Walkthrough"
                  : "Article";
            return (
              <Link
                key={card.id}
                to={buildUrl(`wiki/guides/${card.path}`)}
                className="group flex items-center justify-between px-5 py-3 transition-colors duration-100 hover:bg-[#f8fbfd]"
              >
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8fa8b2]">
                    {card.sectionEyebrow}
                  </p>
                  <p className="mt-0.5 text-[14px] font-medium leading-snug text-[#17313a] transition-colors duration-100 group-hover:text-[#0c98d0]">
                    {card.title}
                  </p>
                </div>
                <div className="ml-4 flex shrink-0 items-center gap-2">
                  {meta && (
                    <span className="text-[11px] text-[#adb8be]">
                      {meta} · {typeLabel}
                    </span>
                  )}
                  <span className="text-[#c0d0d6] transition-colors duration-100 group-hover:text-[#0c98d0]">
                    →
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── WikiHome ──────────────────────────────────────────────────────────────────

const WikiHome = () => {
  const { buildUrl } = useScopeParams();
  const [latestRelease, setLatestRelease] = useState<LatestRelease | null>(
    null,
  );

  useEffect(() => {
    scrollToWikiTop();

    const loadLatestRelease = async () => {
      try {
        const manifest = await getManifest("release-notes");
        const files = (manifest.files ?? [])
          .filter((f: { slug: string }) => f.slug !== "all-release-notes")
          .sort(
            (a: { date?: string }, b: { date?: string }) =>
              manifestDateValue(a.date ?? "") - manifestDateValue(b.date ?? ""),
          );
        const latest = files[files.length - 1] as
          | { slug: string; date: string }
          | undefined;
        if (!latest) return;
        const data = await loadMarkdown(`release-notes/${latest.slug}`);
        if (!data) return;
        setLatestRelease({
          version: String(data.frontmatter.releaseVersion),
          date: formatManifestDate(latest.date),
          description: String(data.frontmatter.description),
        });
      } catch {
        // silently skip if release notes unavailable
      }
    };

    loadLatestRelease();
  }, []);

  return (
    <div className="mx-auto max-w-[1440px] px-8 pb-20">
      <EditorialHero buildUrl={buildUrl} latestRelease={latestRelease} />
      {HUB_SECTIONS.map((section) => (
        <HubSection key={section.key} section={section} buildUrl={buildUrl} />
      ))}
    </div>
  );
};

export default WikiHome;
